<template>
  <SlideIn :open="isOpen" :heading="heading" @close="isOpen = false">
    <template #content>
      <p class="text">
        {{ text }}
      </p>
      <div class="actions">
        <Button full-width tag="a" :href="takeMeThereUrl">
          Take me there
        </Button>
        <Button full-width theme="outline" @click="handleNoThanks">
          No thanks
        </Button>
      </div>
    </template>
  </SlideIn>
</template>

<script>
import * as Sentry from "@sentry/vue";
import { mapActions, mapGetters, mapState } from "vuex";

import Button from "@/components/Button.vue";
import SlideIn from "@/components/SlideIn.vue";
import { localeUrls } from "@/config";
import {
  countryToCurrency,
  euCountries,
  usCountries,
} from "@/helpers/countries";

const REDIRECTION_CONTENT = {
  EU: {
    heading: "We noticed you're in the EU",
    text: "We noticed you're in the EU, please follow the link below to shop our EURO store - taxes are included and there are now no duty charges. Please note customers with EU address must check out through the EURO store. Thank you.",
  },
  AU: {
    heading: "We noticed you're in AU",
    text: "Would you like to shop on our AU store",
  },
  US: {
    heading: "We noticed you're in the US",
    text: "Would you like to shop on our US store",
  },
  GB: {
    heading: "We noticed you're in GB",
    text: "Would you like to shop on our GB store",
  },
};

export default {
  name: "StoreRedirect",
  components: {
    Button,
    SlideIn,
  },
  data() {
    return {
      open: false,
      takeMeThereUrl: null,
      heading: "",
      text: null,
    };
  },
  computed: {
    ...mapState("app", ["shopCountryCode", "userCountry"]),
    ...mapGetters("app", [
      "currencyCode",
      "storeRedirectDismissed",
      "storeAutoRedirected",
    ]),
    isOpen: {
      get() {
        if (this.storeRedirectDismissed) {
          return false;
        }

        if (this.open) {
          return true;
        }

        return false;
      },
      set(value) {
        if (!value && this.open) {
          this.setUserPreferences({
            key: "storeRedirectDismissed",
            value: true,
          });
        }

        this.open = value;
      },
    },
  },
  watch: {
    shopCountryCode(val) {
      console.log("val", val);
      if (val) {
        this.checkCountryCodes();
      }
    },
  },
  methods: {
    ...mapActions("app", ["setUserPreferences"]),
    handleNoThanks() {
      this.isOpen = false;
      this.setUserPreferences({ key: "storeRedirectDismissed", value: true });
    },
    async checkCountryCodes() {
      if (!this.shopCountryCode) {
        return;
      }

      const usersCountryIsInEU = euCountries.includes(this.userCountry);
      const usersCountryIsKindaUS = usCountries.includes(this.userCountry);

      const usersRegion = usersCountryIsInEU
        ? "EU"
        : usersCountryIsKindaUS
        ? "US"
        : this.userCountry;

      const debug = {
        usersCountryCode: this.userCountry,
        shopCountryCode: this.shopCountryCode,
        usersCountryIsInEU,
        usersRegion,
      };

      Sentry.addBreadcrumb({
        category: "region",
        message: JSON.stringify(debug),
        level: "info",
      });

      if (this.shopCountryCode == usersRegion) {
        console.log("shopCountryCode and usersRegion match");
        return;
      }

      if (this.shopCountryCode == "EU" && usersCountryIsInEU) {
        console.log("shopCountryCode is EU and usersCountryIsInEU");
        return;
      }

      const currentPath = window.location.pathname;
      const nostoRemovedPath = currentPath.split(/-\d/)[0];

      function transformQueryString(queryString) {
        const params = new URLSearchParams(queryString);
        const newParams = new URLSearchParams();

        for (const [key, value] of params.entries()) {
          if (key.startsWith("utm_")) {
            newParams.append(key.replace("utm_", "tw_"), value);
          } else {
            newParams.append(key, value);
          }
        }
        return newParams.toString();
      }

      const currentQueryString = window.location.search;
      const transformedQueryString = transformQueryString(currentQueryString);

      const usersCurrency = countryToCurrency[usersRegion];
      this.takeMeThereUrl =
        "https://" +
        localeUrls[usersCurrency] +
        nostoRemovedPath +
        "?" +
        transformedQueryString;

      this.heading = REDIRECTION_CONTENT[usersRegion]?.heading || null;
      this.text = REDIRECTION_CONTENT[usersRegion]?.text || null;

      if (this.heading && this.text) {
        if (!this.storeAutoRedirected) {
          this.setUserPreferences({
            key: "storeAutoRedirected",
            value: true,
          });
          window.location.replace(this.takeMeThereUrl);
        } else {
          this.isOpen = true;
        }
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.actions {
  margin: 0 -20px 0;
  padding: 0 20px 0;

  :deep(.button) {
    &:first-child {
      margin: 0 0 10px 0;
    }
  }
}

.text {
  font-family: $heading-font-family;
  font-size: 12px;
  font-weight: 300;
  margin: 10px 0 30px 0;
}
</style>
